/**
 * Typed errors for the shared API client.
 *
 * The backend returns a consistent error envelope (see design.md "Error
 * Handling"):
 *
 * ```json
 * { "error": "HITS_OUT_OF_RANGE", "message": "Hits must be between 0 and 10" }
 * ```
 *
 * The client maps every non-2xx response to an {@link ApiError} carrying the
 * HTTP `statusCode`, the machine-readable `error` code, and the human-readable
 * `message`, so callers can branch on a stable code rather than parsing strings.
 */
/** Type guard for the `{ error, message }` envelope. */
export function isApiErrorEnvelope(value) {
    return (typeof value === 'object' &&
        value !== null &&
        'error' in value &&
        typeof value.error === 'string');
}
/**
 * Error thrown by the API client for any non-2xx response (or a transport
 * failure). Carries the HTTP status, the backend error code, and the message.
 */
export class ApiError extends Error {
    /** HTTP status code (e.g. 401, 403, 404, 409, 422). `0` for transport/network failures. */
    statusCode;
    /** Machine-readable error code from the envelope (`error` field), or a synthetic code for transport failures. */
    code;
    /** The parsed response body, when JSON was available. */
    body;
    constructor(params) {
        super(params.message);
        this.name = 'ApiError';
        this.statusCode = params.statusCode;
        this.code = params.code;
        this.body = params.body;
        // Restore prototype chain for instanceof across transpilation targets.
        Object.setPrototypeOf(this, ApiError.prototype);
    }
    /**
     * True when the backend reported that authentication is required — e.g. an
     * unauthenticated request to the public leaderboard of a non-public event,
     * which the design specifies as `403 { error: "AUTHENTICATION_REQUIRED" }`.
     */
    get isAuthenticationRequired() {
        return this.code === 'AUTHENTICATION_REQUIRED';
    }
    /** True for client-side authorization denials (not-admin, no-permission, etc.). */
    get isForbidden() {
        return this.statusCode === 403;
    }
    /** True for validation failures (HTTP 422). */
    get isValidation() {
        return this.statusCode === 422;
    }
    /** True for not-found responses (HTTP 404). */
    get isNotFound() {
        return this.statusCode === 404;
    }
}
//# sourceMappingURL=errors.js.map